import {
  rulesetQuery,
  rulesetVersionsQuery,
  useCreateRulesetVersionMutation,
} from "@common/api/quoting/ruleset"
import type {
  QuoteRulesetDetailPublic,
  QuoteRulesetVersionApplicabilityDetailPublic,
  QuoteRulesetVersionCreate,
  QuoteRulesetVersionDetailPublic,
  QuoteRulesetVersionRuleCreate,
} from "@common/client"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import useCustomToast from "@/hooks/use-custom-toast"
import { aircraftSubject, customerSubject } from "../../-components/form/schema"

type Version = QuoteRulesetVersionDetailPublic

type LatestVersion = QuoteRulesetDetailPublic["latest_version"]

type VersionApplicability = NonNullable<
  QuoteRulesetVersionCreate["applicability"]
>[number]

export type Subjects = {
  customer_id: string | null
  type_certificate_model_variant_id: string | null
}

export function usePlaybook(playbookId: string): {
  playbook: QuoteRulesetDetailPublic
  versions: Version[]
} {
  const { data: playbook } = useSuspenseQuery(
    rulesetQuery({ ruleset_id: playbookId }),
  )
  const { data: versions } = useSuspenseQuery(
    rulesetVersionsQuery({ ruleset_id: playbookId }),
  )
  return { playbook, versions: versions.data }
}

export function useSaveVersion(playbookId: string): {
  save: (
    body: QuoteRulesetVersionCreate,
    title: string,
  ) => Promise<Version | undefined>
  pending: boolean
} {
  const mutation = useCreateRulesetVersionMutation()
  const showToast = useCustomToast()
  const { refetch: refetchPlaybook } = useSuspenseQuery(
    rulesetQuery({ ruleset_id: playbookId }),
  )
  const { refetch: refetchVersions } = useSuspenseQuery(
    rulesetVersionsQuery({ ruleset_id: playbookId }),
  )

  const save = (body: QuoteRulesetVersionCreate, title: string) =>
    mutation
      .mutateAsync({ ruleset_id: playbookId, quoteRulesetVersionCreate: body })
      .then((saved) => {
        showToast(title, `Saved as version ${saved.number}.`, "success")
        return saved
      })
      .catch((error) => {
        handleError(error, showToast)
        return Promise.all([refetchPlaybook(), refetchVersions()]).then(
          () => undefined,
        )
      })

  return { save, pending: mutation.isPending }
}

function currentRules(version: LatestVersion): QuoteRulesetVersionRuleCreate[] {
  return (version?.active_rules ?? []).map(({ content, lineage_id }) => ({
    content,
    lineage_id,
  }))
}

function subjectCreate(
  row: QuoteRulesetVersionApplicabilityDetailPublic,
): VersionApplicability[] {
  switch (row.subject) {
    case "aircraft":
      return aircraftSubject(row.type_certificate_model_variant_id ?? null)
    case "customer":
      return customerSubject(row.customer_id ?? null)
    case "part":
      return row.part_id ? [{ subject: "part", part_id: row.part_id }] : []
  }
}

function currentApplicability(version: LatestVersion): VersionApplicability[] {
  return (version?.active_applicability ?? []).flatMap(subjectCreate)
}

export function withRuleAdded(
  version: LatestVersion,
  content: string,
): QuoteRulesetVersionCreate {
  return {
    rules: [...currentRules(version), { content }],
    applicability: currentApplicability(version),
    previous_version_id: version?.id ?? null,
  }
}

export function withRuleEdited(
  version: Version,
  lineageId: string,
  content: string,
): QuoteRulesetVersionCreate {
  return {
    rules: currentRules(version).map((rule) =>
      rule.lineage_id === lineageId ? { ...rule, content } : rule,
    ),
    applicability: currentApplicability(version),
    previous_version_id: version.id,
  }
}

export function withRuleRemoved(
  version: Version,
  lineageId: string,
): QuoteRulesetVersionCreate {
  return {
    rules: currentRules(version).filter(
      (rule) => rule.lineage_id !== lineageId,
    ),
    applicability: currentApplicability(version),
    previous_version_id: version.id,
  }
}

export function withSubjects(
  version: Version,
  subjects: Subjects,
): QuoteRulesetVersionCreate {
  return {
    rules: currentRules(version),
    applicability: [
      ...customerSubject(subjects.customer_id),
      ...aircraftSubject(subjects.type_certificate_model_variant_id),
    ],
    previous_version_id: version.id,
  }
}

export function currentSubjects(
  version: LatestVersion,
): Subjects & { editable: boolean } {
  const rows = version?.active_applicability ?? []
  const customers = rows.filter(({ subject }) => subject === "customer")
  const aircraft = rows.filter(({ subject }) => subject === "aircraft")
  const parts = rows.filter(({ subject }) => subject === "part")

  return {
    editable: customers.length <= 1 && aircraft.length <= 1 && !parts.length,
    customer_id: customers[0]?.customer_id ?? null,
    type_certificate_model_variant_id:
      aircraft[0]?.type_certificate_model_variant_id ?? null,
  }
}
